import React, { useMemo, useState } from "react";
import { Box, Button, Flex, FlexList, useI18nContext } from "@procore/core-react";
import type { RequestListDataTableProps } from "./request-list-prop-types";
import { useProjectTearsheet } from "../tearsheets/project/project-tearsheet";
import { useCallback, useEffect, useRef } from "react";
import type {
   CellValueChangeParams,
   ColumnDefinition,
   DataTableCellRendererProps,
   DataTableConfig,
   ServerSideGetRowsParams,
} from "@procore/data-table";
import { ServerSideDataTable } from "@procore/data-table";
import type { IntegratedField, SortModel } from "@/react/prop-types";
import { PageTitle, ResourceType } from "@/react/prop-types";
import {
   columnHeadersMap,
   defaultRequestListTableConfig,
   fetchAllRequests,
   filterFieldMap,
   filterNameMaps,
   filterRendererMap,
   mapEquipmentTypesToColorSelectOptions,
   updateInLineRequest,
} from "./request-list-helpers";
import type { ColorSelectValue } from "../data-table/ColorSelectComponent/ColorSelectColumn";
import {
   getReorderedColumnDefinition,
   convertDataTableConfigToSavedView,
} from "@/react/shared/helper";
import { reportError } from "@/utils/logging-utils";
import type { ListServerFilter } from "../data-table/data-table-utils";
import {
   haveFiltersChanged,
   prepareConfigForStorage,
   streamJobTitles,
} from "../data-table/data-table-utils";
import { createColumnDefinitionsFromCustomHeaders } from "@/react/shared/custom-field-utils";
import { AuthAction, usePermissionContext } from "@/react/providers/permission-context-provider";
import { RequestStore } from "@/stores/request-store.core";
import { ConfirmDeleteModal } from "@/react/shared/modals/confirm-delete-modal";
import { Pencil, PersonPlus, Plus, Snapshot } from "@procore/core-icons";
import {
   onCreateRequestClick,
   onEditRequestClick,
   onFillRequestClick,
   useRequestTearsheet,
} from "../tearsheets/request/request-tearsheet";
import {
   useGetEquipmentTypes,
   useGetIntegratedFields,
   useGetTagOptionsQuery,
} from "../common/queries/queries";
import type { ExportFormValues } from "../data-table/DataExport/export-modal";
import { ExportModal } from "../data-table/DataExport/export-modal";
import { useExportToastContext } from "../data-table/DataExport/FileDownload";
import { createRequestListReport } from "./queries";
import { DEFAULT_START_TIME } from "@/react/shared/constants";
import { getOrderedTimeOptions } from "../tearsheets/time-off/helpers";
import { CreateSavedViewModal } from "../modals/saved-view/create-saved-view-modal";
import { SavedViewPage } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { useToastAlertContext } from "@procore/toast-alert";
import { BulkActionsBar } from "../data-table/BulkActionsBar";
import { BulkEditRequestTearsheet } from "../tearsheets/request/bulk-edit-request-tearsheet";
import { EquipmentRequestStore } from "@/stores/equipment-requests-store.core";
import { useViewableStatusesOptions } from "../assignment-list/queries";
import { DataTableToolbarQuickFilters } from "../data-table/DataTableToolbarQuickFilters";
import { useRequestListColumnDefinitions } from "./request-list-column-definitions";

const LOCAL_STORAGE_CONFIG_KEY = "requestListTableConfig";
const LOCAL_STORAGE_SAVED_VIEW_KEY = "requestListSavedViewConfig";

export const RequestListDataTable = (props: RequestListDataTableProps) => {
   const I18n = useI18nContext();
   const { data: integratedFields } = useGetIntegratedFields();
   const [customColumnDefinitions, setCustomColumnDefinitions] = useState<ColumnDefinition[]>([]);
   const [projectIntegratedFields, setProjectIntegratedFields] = useState<IntegratedField[]>([]);
   const [peopleIntegratedFields, setPeopleIntegratedFields] = useState<IntegratedField[]>([]);
   const [jobTitleOptions, setJobTitleOptions] = useState<ColorSelectValue[]>([]);

   const orderedTimeOptions = getOrderedTimeOptions(DEFAULT_START_TIME);

   const {
      fetchRequestList,
      tableApi,
      handleTableReady,
      groupId,
      customFields,
      isLastNameFirst,
      savedView,
      groupOptions,
      assignmentCreationSupportData,
   } = props;

   //refreshCustomFields is maintained to trigger customFields useEffect when component rerenders on some event like edit button clicked
   const [refreshCustomFields, setRefreshCustomFields] = useState(false);

   const { checkAuthAction } = usePermissionContext();
   const [canManageRequests, setCanManageRequests] = useState(false);

   const { dispatch: projectTearsheetDispatch } = useProjectTearsheet();
   const { dispatch: requestTearsheetDispatch } = useRequestTearsheet();
   const { data: viewableStatusesOptions } = useViewableStatusesOptions(groupId);

   const { data: tags, isLoading: tagsIsLoading } = useGetTagOptionsQuery();
   const { setVisible: showFileDownloadToast, addItems: addDownloadableReport } =
      useExportToastContext();
   const { data: equipmentTypes } = useGetEquipmentTypes();
   const equipmentTitleOptions = mapEquipmentTypesToColorSelectOptions(equipmentTypes);

   const [manuallySelectedRowData, setManuallySelectedRowData] = useState<any>([]);
   const [selectionInProgress, setSelectionInProgress] = useState(false);

   const { showToast } = useToastAlertContext();

   const searchValueRef = useRef<string>("");
   const previousSearchRef = useRef<string>("");

   // Next starting_after value for pagination
   const nextStartAfter = useRef(undefined);

   // reading tableConfig like this to avoid re-renders for server side filters they kind of reset when re-rendered
   const cachedConfig =
      JSON.parse(localStorage.getItem(LOCAL_STORAGE_CONFIG_KEY)!) ?? defaultRequestListTableConfig;

   const tableConfig = savedView ?? cachedConfig;

   // Track previous filters to detect actual filter changes (vs other config changes)
   const previousFiltersRef = useRef<ListServerFilter[]>(tableConfig.serverFilters ?? []);

   useEffect(() => {
      const canManageRequests = checkAuthAction(AuthAction.MANAGE_REQUESTS);
      setCanManageRequests(canManageRequests);
   }, [checkAuthAction]);

   useEffect(() => {
      if (tableApi) {
         tableApi.refreshServerSide({});
      }
   }, [groupId]);

   useEffect(() => {
      if (integratedFields) {
         if (Array.isArray(integratedFields.data.projects_integrated_fields)) {
            setProjectIntegratedFields(integratedFields.data.projects_integrated_fields);
         }
         if (Array.isArray(integratedFields.data.people_integrated_fields)) {
            const updatedFields = integratedFields.data.people_integrated_fields.map((field) => {
               switch (field.property) {
                  case "first_name":
                  case "last_name":
                     return { ...field, property: "person" };
                  case "is_male":
                     return { ...field, property: "gender" };
                  default:
                     return field;
               }
            });

            setPeopleIntegratedFields(updatedFields);
         }
      }
   }, [integratedFields]);

   useEffect(() => {
      if (streamJobTitles) {
         const fetchJobTitlesOptions = async () => {
            const jobTitlesOptions = await streamJobTitles(groupId);
            if (jobTitlesOptions) {
               const options = jobTitlesOptions.map((jobTitle) => {
                  return {
                     ...jobTitle,
                     shape: "circle",
                  };
               }) as ColorSelectValue[];

               setJobTitleOptions(options);
            }
         };

         fetchJobTitlesOptions();
      }
   }, [groupId, streamJobTitles]);

   const onTableConfigChange = useCallback(
      (config: DataTableConfig) => {
         const preparedConfig = prepareConfigForStorage(
            config,
            customFields,
            customColumnDefinitions,
         );
         const storageKey = savedView ? LOCAL_STORAGE_SAVED_VIEW_KEY : LOCAL_STORAGE_CONFIG_KEY;
         localStorage.setItem(storageKey, JSON.stringify(preparedConfig));
      },
      [customFields, customColumnDefinitions, savedView],
   );

   const handleCellValueChanged = useCallback(
      async (params: CellValueChangeParams<any>) => {
         try {
            await updateInLineRequest(params, customFields ?? []);
            // Refresh so grouped view reflects the change without full page reload
            tableApi?.refreshServerSide({ purge: true });
         } catch (error) {
            reportError(error as Error, "request-list__handleCellValueChanged");
         }
      },
      [tableApi],
   );

   const onRequestListExport = React.useCallback(
      async (params: ExportFormValues) => {
         const report = await createRequestListReport({
            formValues: params,
            groupId,
            I18n,
            customFields,
            configKey: savedView ? LOCAL_STORAGE_SAVED_VIEW_KEY : LOCAL_STORAGE_CONFIG_KEY,
            isLastNameFirst,
            search: searchValueRef.current ?? null,
         });
         showFileDownloadToast(true);
         addDownloadableReport([report.data]);
      },
      [tableConfig],
   );
   /* istanbul ignore next */
   const Delete = (props: DataTableCellRendererProps) => {
      const { request_type } = props.data;
      // if the request is an equipment request, use the equipment request store to delete the request otherwise use the request store
      const onDeleteStore =
         request_type === ResourceType.EQUIPMENT
            ? EquipmentRequestStore.deleteEquipmentRequest
            : RequestStore.deleteRequest;
      return (
         <ConfirmDeleteModal
            id={props.data.id}
            tableApi={tableApi}
            refreshParams={{ purge: true }}
            headerText={"views.company.workforce_planning.request.modal.delete.header"}
            deleteText={"views.company.workforce_planning.request.modal.delete.description"}
            successText={"views.company.workforce_planning.request.modal.delete.success"}
            errorText={"views.company.workforce_planning.request.modal.delete.error"}
            onDelete={onDeleteStore}
            store={request_type === ResourceType.EQUIPMENT ? EquipmentRequestStore : RequestStore}
         />
      );
   };

   const Edit = (props: DataTableCellRendererProps) => {
      const { id, request_type } = props.data;

      const handleEdit = () => {
         setRefreshCustomFields(!refreshCustomFields);
         // passing true for isFromRequestList flag to indicate that the request is from the request list to not disturb the gantt and boards flow
         onEditRequestClick(requestTearsheetDispatch, id, request_type, true);
      };

      return (
         <Button
            aria-label="edit"
            icon={<Pencil />}
            onClick={() => handleEdit()}
            variant="tertiary"
         />
      );
   };

   const Fill = (props: DataTableCellRendererProps) => {
      const { id, request_type, equipment_type } = props.data;
      const isLaborRequest = request_type === ResourceType.LABOR;
      const handleFill = () => {
         setRefreshCustomFields(!refreshCustomFields);
         // passing true to the onEditRequestClick to indicate that the request is from the request list.
         onFillRequestClick(
            requestTearsheetDispatch,
            id,
            request_type,
            equipment_type?.id ?? null,
            true,
         );
      };

      return (
         <Button
            aria-label={
               isLaborRequest
                  ? I18n.t("views.company.workforce_planning.request.fill_labor_request")
                  : I18n.t("views.company.workforce_planning.request.fill_equipment_request")
            }
            icon={isLaborRequest ? <PersonPlus /> : <Snapshot />}
            onClick={() => handleFill()}
            variant="tertiary"
         />
      );
   };

   const requestColumnDefinitions = useRequestListColumnDefinitions({
      I18n,
      canManageRequests,
      groupId,
      orderedTimeOptions,
      projectIntegratedFields,
      peopleIntegratedFields,
      jobTitleOptions,
      equipmentTitleOptions,
      viewableStatusesOptions: viewableStatusesOptions ?? [],
      projectTearsheetDispatch,
      tags: tags ?? [],
   });

   useEffect(() => {
      if (customFields) {
         // since the sensitive fields for requests are not implemented yet, we are passing false for canViewSensitiveFields and canEditSensitiveFields
         const sensitiveFieldsDetails = {
            canViewSensitiveFields: false,
            canEditSensitiveFields: false,
            sensitiveFields: [],
         };
         const customColumnDefinitions = createColumnDefinitionsFromCustomHeaders(
            customFields,
            tableConfig,
            projectIntegratedFields,
            null,
            sensitiveFieldsDetails,
            true,
            canManageRequests,
            PageTitle.REQUEST_LIST,
         );
         if (customColumnDefinitions.length > 0) {
            setCustomColumnDefinitions(customColumnDefinitions);
         }
      }
   }, [customFields, groupId, refreshCustomFields, canManageRequests]);

   const handleServerSideDataRequest = useCallback(
      async ({ request, onSuccess, onError }: ServerSideGetRowsParams) => {
         try {
            const { groupKeys, rowGroupCols } = request;
            let startingAfter = undefined;
            // If requesting a second page of data, apply the starting_after query param
            if ((request.startRow ?? 0) > 0) {
               startingAfter = nextStartAfter.current;
            }
            const serverFilter: ListServerFilter[] = request.serverFilters ?? [];
            searchValueRef.current = request.searchValue?.trim();
            // Always use a defined, trimmed search string
            const searchString = searchValueRef.current;
            const sortModel: SortModel[] = request.sortModel ?? [];

            const { data, pagination } = await fetchRequestList(
               startingAfter,
               serverFilter,
               searchString,
               sortModel,
               groupKeys,
               rowGroupCols,
            );
            onSuccess({
               rowData: data,
               rowCount: rowGroupCols?.length ? data.length : pagination.total_possible,
            });
            nextStartAfter.current = pagination.next_starting_after;

            // Clear selections when filters change (after table has new data)
            if (haveFiltersChanged(serverFilter, previousFiltersRef.current)) {
               setManuallySelectedRowData([]);
               tableApi?.deselectAll();
               previousFiltersRef.current = serverFilter;
            }

            // Clear selections when search value changes
            if (searchString !== previousSearchRef.current) {
               setManuallySelectedRowData([]);
               tableApi?.deselectAll();
               previousSearchRef.current = searchString;
            }
         } catch (err) {
            onError();
         }
      },
      [groupId, tableApi, fetchRequestList],
   );

   //Reorder the columns based on the saved column state to maintain column order
   const reorderedRequestColumnDefinitions = useMemo(
      () =>
         getReorderedColumnDefinition(
            customColumnDefinitions
               ? requestColumnDefinitions.concat(customColumnDefinitions)
               : requestColumnDefinitions,
            tableConfig.columnState,
         ),
      [requestColumnDefinitions, tableConfig.columnState, customColumnDefinitions],
   );

   return (
      !tagsIsLoading && (
         <ServerSideDataTable
            columnDefinitions={reorderedRequestColumnDefinitions}
            onServerSideDataRequest={handleServerSideDataRequest}
            getRowId={(params) => params.data.id}
            onTableConfigChange={onTableConfigChange}
            initialTableConfig={tableConfig}
            enableDynamicRowHeight
            key={groupId}
            showExpandCollapseAllToggle
         >
            <Flex
               style={{
                  height: "100%",
                  width: "100%",
               }}
               alignItems="stretch"
            >
               <Flex flex="0 1 0px" alignItems="stretch" paddingRight="md">
                  <ServerSideDataTable.FiltersPanel />
               </Flex>

               <Box flex="1" display="flex-column" alignItems="stretch">
                  <ServerSideDataTable.QuickControls>
                     <Flex>
                        <FlexList size="xs" alignItems="center">
                           <ServerSideDataTable.Search
                              placeholder={I18n.t(
                                 "views.company.workforce_planning.request.search_placeholder",
                              )}
                           />
                           <ServerSideDataTable.FiltersPanelButton />
                        </FlexList>
                        <DataTableToolbarQuickFilters />
                        <FlexList size="xs" alignItems="center" style={{ marginLeft: "auto" }}>
                           {canManageRequests && (
                              <Button
                                 onClick={() => {
                                    onCreateRequestClick(requestTearsheetDispatch);
                                    setRefreshCustomFields(!refreshCustomFields);
                                 }}
                                 icon={<Plus />}
                              >
                                 {I18n.t("views.company.workforce_planning.create")}
                              </Button>
                           )}
                           <ExportModal onExport={onRequestListExport} />
                           <CreateSavedViewModal
                              configKey={
                                 savedView ? LOCAL_STORAGE_SAVED_VIEW_KEY : LOCAL_STORAGE_CONFIG_KEY
                              }
                              tableApi={tableApi}
                              conversionFunction={convertDataTableConfigToSavedView}
                              page={SavedViewPage.REQUESTS}
                              customFields={customFields ?? []}
                              columnHeadersMap={columnHeadersMap}
                              filterNameMaps={filterNameMaps}
                              filterFieldMap={filterFieldMap}
                              filterRendererMap={filterRendererMap}
                              searchString={searchValueRef}
                              groupId={groupId}
                              groupOptions={groupOptions ?? []}
                           />
                           <ServerSideDataTable.ConfigPanelButton />
                        </FlexList>
                     </Flex>
                  </ServerSideDataTable.QuickControls>
                  <BulkActionsBar
                     selectedRows={manuallySelectedRowData}
                     selectionInProgress={selectionInProgress}
                  >
                     {canManageRequests && (
                        <Flex alignItems="center" gap="sm">
                           <BulkEditRequestTearsheet
                              tableApi={tableApi}
                              customFields={customFields ?? []}
                              selectedRows={manuallySelectedRowData}
                              selectionInProgress={selectionInProgress}
                              setManuallySelectedRowData={setManuallySelectedRowData}
                              jobTitles={jobTitleOptions}
                              assignmentCreationSupportData={assignmentCreationSupportData}
                              peopleIntegratedFields={peopleIntegratedFields}
                           />

                           <ConfirmDeleteModal
                              isBulk
                              tableApi={tableApi}
                              refreshParams={{ purge: true }}
                              headerText={
                                 "views.company.workforce_planning.request.modal.bulk_delete.header"
                              }
                              deleteText={
                                 "views.company.workforce_planning.request.modal.bulk_delete.description"
                              }
                              successText={
                                 "views.company.workforce_planning.request.modal.bulk_delete.success"
                              }
                              errorText={
                                 "views.company.workforce_planning.request.modal.bulk_delete.error"
                              }
                              selectedRowData={manuallySelectedRowData}
                              selectionInProgress={selectionInProgress}
                              setManuallySelectedRowData={setManuallySelectedRowData}
                              onDelete={RequestStore.batchDelete}
                              store={RequestStore}
                           />
                        </Flex>
                     )}
                  </BulkActionsBar>
                  <ServerSideDataTable.Table
                     onTableReady={handleTableReady}
                     paginationPageSize={100}
                     rowActions={canManageRequests ? [Delete, Fill, Edit] : []}
                     suppressColumnVirtualisation={false}
                     rowActionsConfig={{ sortable: false }}
                     groupDefaultExpanded={-1}
                     hideActionsOnGroupedRow
                     suppressColumnGroupBorders={true}
                     onCellValueChanged={handleCellValueChanged}
                     rowSelectionEnabled={(node) => {
                        if (!canManageRequests || !node?.data) return false;
                        // show checkbox on grouped (project) header rows, like people list
                        if (node.data.excluded) return true;
                        // show checkbox only on labor request rows
                        return node.data.request_type === ResourceType.LABOR;
                     }}
                     selectionSSREnabled={canManageRequests}
                     onRowSelected={(event) => {
                        // Skip if this is from API select all
                        if (event?.source === "apiSelectAll") {
                           return;
                        }

                        if (event.node.isSelected()) {
                           setManuallySelectedRowData((prev: any) => [...prev, event.node.data]);
                        } else {
                           setManuallySelectedRowData((prev: any) =>
                              prev.filter((person: any) => person.id !== event.node.data.id),
                           );
                        }
                     }}
                     onSelectAll={async (params) => {
                        if (params !== "all") {
                           setManuallySelectedRowData([]);
                           setSelectionInProgress(false);
                           setRefreshCustomFields(true);
                           return;
                        }
                        setSelectionInProgress(true);
                        const configKey = savedView
                           ? LOCAL_STORAGE_SAVED_VIEW_KEY
                           : LOCAL_STORAGE_CONFIG_KEY;
                        try {
                           const dataTableRecords = await fetchAllRequests({
                              configKey,
                              groupId,
                              search: searchValueRef.current ?? undefined,
                              isLastNameFirst,
                              assignmentCreationSupportData,
                           });
                           setManuallySelectedRowData(dataTableRecords);
                        } catch {
                           showToast.error(
                              I18n.t(
                                 "views.company.workforce_planning.data_table.bulk_actions.error",
                              ),
                           );
                           setManuallySelectedRowData([]);
                           tableApi?.deselectAll();
                        } finally {
                           setSelectionInProgress(false);
                           setRefreshCustomFields(true);
                        }
                     }}
                  />
               </Box>
               <Flex flex="0 1 0px" alignItems="stretch" paddingLeft="md">
                  <ServerSideDataTable.ContextPanel />
               </Flex>
            </Flex>
         </ServerSideDataTable>
      )
   );
};
